import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Package, Loader2, Truck, RefreshCw, AlertCircle, CheckCircle2, Clock, Filter, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API = '/api';

function StockCard({ item }) {
  const atpColor = item.available_to_promise > 0 ? 'text-green-600' : 'text-red-500';
  const deliveryColor = item.estimated_delivery_days <= 7
    ? 'text-green-600'
    : item.estimated_delivery_days <= 30
      ? 'text-amber-600'
      : 'text-red-500';

  const statusIcon = item.available_to_promise > 0
    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
    : item.promised > 0
      ? <Clock className="h-4 w-4 text-amber-500" />
      : <AlertCircle className="h-4 w-4 text-red-500" />;

  const statusLabel = item.available_to_promise > 0
    ? 'In Stock'
    : item.promised > 0
      ? 'Committed'
      : 'Out of Stock';

  return (
    <div
      data-testid={`stock-card-${item.sku_id}`}
      className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white dark:bg-slate-900"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-lg">{item.kva} KVA</span>
            <Badge variant="outline">{item.phase}</Badge>
          </div>
          {item.model && <p className="text-sm text-muted-foreground">{item.model}</p>}
          <p className="text-xs text-muted-foreground mt-1">SKU: {item.sku_id}</p>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          {statusIcon}
          <span className={cn("font-medium", item.available_to_promise > 0 ? 'text-green-600' : item.promised > 0 ? 'text-amber-600' : 'text-red-500')}>
            {statusLabel}
          </span>
        </div>
      </div>

      <Separator className="mb-3" />

      <div className="grid grid-cols-4 gap-3 text-center">
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Available</p>
          <p className={cn("text-xl font-bold", atpColor)}>{item.available_to_promise}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Promised</p>
          <p className="text-xl font-bold text-amber-600">{item.promised}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">{item.in_production != null ? 'In Production' : 'Total'}</p>
          <p className="text-xl font-bold">{item.in_production != null ? item.in_production : item.total_stock}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Delivery</p>
          <p className={cn("text-xl font-bold flex items-center justify-center gap-1", deliveryColor)}>
            <Truck className="h-4 w-4" />
            {item.estimated_delivery_days}d
          </p>
        </div>
      </div>

      {item.last_synced && (
        <p className="text-xs text-muted-foreground mt-3 text-right">
          Synced: {new Date(item.last_synced).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
        </p>
      )}
    </div>
  );
}

export default function StockChecker() {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ kvas: [], phases: [], models: [] });
  const [selectedKva, setSelectedKva] = useState('all');
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/erp-sync/stock/filters`, { withCredentials: true });
      setFilters(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedKva !== 'all') params.append('kva', selectedKva);
      if (selectedPhase !== 'all') params.append('phase', selectedPhase);
      const res = await axios.get(`${API}/erp-sync/stock?${params}`, { withCredentials: true });
      setStock(res.data.stock || []);
    } catch {
      toast.error('Failed to load stock data');
      setStock([]);
    } finally {
      setLoading(false);
    }
  }, [selectedKva, selectedPhase]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchStock(); }, [fetchStock]);

  const handleSyncERP = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/erp-sync/stock/sync`, {}, { withCredentials: true });
      if (res.data.status === 'ok') {
        toast.success(`Stock synced: ${res.data.updated} items updated`);
        fetchStock();
        fetchFilters();
      } else {
        toast.info(res.data.reason || 'No updates from ERP');
      }
    } catch {
      toast.error('Failed to sync from ERP');
    } finally {
      setSyncing(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/erp-sync/stock/seed`, {}, { withCredentials: true });
      toast.success(`Seeded ${res.data.seeded} stock entries`);
      fetchStock();
      fetchFilters();
    } catch {
      toast.error('Failed to seed stock data');
    } finally {
      setSeeding(false);
    }
  };

  const clearFilters = () => {
    setSelectedKva('all');
    setSelectedPhase('all');
  };

  const hasFilters = selectedKva !== 'all' || selectedPhase !== 'all';

  // Summary stats
  const totalATP = stock.reduce((s, i) => s + (i.available_to_promise || 0), 0);
  const totalPromised = stock.reduce((s, i) => s + (i.promised || 0), 0);
  const outOfStock = stock.filter(i => i.available_to_promise === 0 && i.promised === 0).length;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="stock-checker-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" />
            Stock Availability
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time inventory from ERP system
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="sync-erp-btn"
            variant="outline"
            onClick={handleSyncERP}
            disabled={syncing}
            className="gap-1"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync from ERP
          </Button>
          {stock.length === 0 && (
            <Button
              data-testid="seed-stock-btn"
              onClick={handleSeedData}
              disabled={seeding}
              className="gap-1"
            >
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
              Initialize Stock
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="border rounded-lg p-4 bg-white dark:bg-slate-900">
          <p className="text-xs text-muted-foreground mb-1">Total SKUs</p>
          <p className="text-2xl font-bold">{stock.length}</p>
        </div>
        <div className="border rounded-lg p-4 bg-white dark:bg-slate-900">
          <p className="text-xs text-muted-foreground mb-1">Available to Promise</p>
          <p className="text-2xl font-bold text-green-600">{totalATP}</p>
        </div>
        <div className="border rounded-lg p-4 bg-white dark:bg-slate-900">
          <p className="text-xs text-muted-foreground mb-1">Promised / Reserved</p>
          <p className="text-2xl font-bold text-amber-600">{totalPromised}</p>
        </div>
        <div className="border rounded-lg p-4 bg-white dark:bg-slate-900">
          <p className="text-xs text-muted-foreground mb-1">Out of Stock</p>
          <p className={cn("text-2xl font-bold", outOfStock > 0 ? "text-red-500" : "text-green-600")}>{outOfStock}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4" data-testid="stock-filters">
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1"><Filter className="h-3 w-3" /> KVA Rating</Label>
          <Select value={selectedKva} onValueChange={setSelectedKva}>
            <SelectTrigger className="w-[150px]" data-testid="stock-filter-kva">
              <SelectValue placeholder="All KVA" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All KVA</SelectItem>
              {filters.kvas.map(k => (
                <SelectItem key={k} value={String(k)}>{k} KVA</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Phase</Label>
          <Select value={selectedPhase} onValueChange={setSelectedPhase}>
            <SelectTrigger className="w-[150px]" data-testid="stock-filter-phase">
              <SelectValue placeholder="All Phases" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Phases</SelectItem>
              {filters.phases.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
            <X className="h-3 w-3" /> Clear
          </Button>
        )}
      </div>

      {/* Stock Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mr-3" />
          Loading stock data...
        </div>
      ) : stock.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No stock data available</p>
          <p className="text-sm mt-1">Click "Initialize Stock" to seed data from your lead database, or "Sync from ERP" to pull from the ERP system.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {stock.map(item => (
            <StockCard key={item.sku_id || `${item.kva}-${item.phase}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
